import { apiRequest } from './client'
import type {
  AgentReleaseListResponse,
  AgentReleaseSummary,
  AgentUpdateRolloutDetail,
  AgentUpdateRolloutFilters,
  AgentUpdateRolloutPreviewResponse,
  CreateAgentUpdateRolloutRequest,
  CreateCommandResponse,
  TriggerAgentUpdateRequest,
} from '@/types'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000'

export async function listReleases(): Promise<AgentReleaseListResponse> {
  return apiRequest<AgentReleaseListResponse>('/api/admin/agent-releases')
}

export async function deleteRelease(version: string): Promise<void> {
  await apiRequest<void>(`/api/admin/agent-releases/${encodeURIComponent(version)}`, {
    method: 'DELETE',
  })
}

/**
 * Upload a new .deb release. Uses multipart/form-data so we bypass the
 * standard apiRequest (which forces Content-Type: application/json).
 *
 * Returns the created release on success.
 *
 * onProgress is called with values in [0, 1] during the upload phase only
 * (not the server-side sha256 / DB insert). Implemented via XMLHttpRequest
 * since the fetch API still lacks first-class upload progress events.
 */
export async function uploadRelease(args: {
  version: string
  channel: string
  notes?: string
  file: File
  onProgress?: (fraction: number) => void
}): Promise<AgentReleaseSummary> {
  const { version, channel, notes, file, onProgress } = args
  const token = localStorage.getItem('auth_token') ?? ''

  const form = new FormData()
  form.append('version', version)
  form.append('channel', channel)
  if (notes) form.append('notes', notes)
  form.append('file', file)

  return await new Promise<AgentReleaseSummary>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}/api/admin/agent-releases`)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded / e.total)
      }
    }
    xhr.onerror = () => reject({ status: 0, message: 'network error' })
    xhr.onload = () => {
      if (xhr.status === 201 || xhr.status === 200) {
        try {
          resolve(JSON.parse(xhr.responseText) as AgentReleaseSummary)
        } catch {
          reject({ status: xhr.status, message: 'invalid response' })
        }
        return
      }
      if (xhr.status === 401) {
        window.dispatchEvent(new Event('auth:unauthenticated'))
      }
      let message = `HTTP ${xhr.status}`
      try {
        const body = JSON.parse(xhr.responseText)
        if (body?.detail) {
          message =
            typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
        }
      } catch {
        // ignore
      }
      reject({ status: xhr.status, message })
    }
    xhr.send(form)
  })
}

export async function triggerAgentUpdate(
  nodeId: string,
  body: TriggerAgentUpdateRequest,
): Promise<CreateCommandResponse> {
  return apiRequest<CreateCommandResponse>(`/api/nodes/${nodeId}/agent-update`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function previewAgentUpdateRollout(
  version: string,
  filters: AgentUpdateRolloutFilters,
  excludeNodeIds: string[] = [],
): Promise<AgentUpdateRolloutPreviewResponse> {
  return apiRequest<AgentUpdateRolloutPreviewResponse>(
    `/api/admin/agent-releases/${encodeURIComponent(version)}/rollouts/preview`,
    {
      method: 'POST',
      body: JSON.stringify({ filters, exclude_node_ids: excludeNodeIds }),
    },
  )
}

export async function createAgentUpdateRollout(
  version: string,
  body: CreateAgentUpdateRolloutRequest,
): Promise<AgentUpdateRolloutDetail> {
  return apiRequest<AgentUpdateRolloutDetail>(
    `/api/admin/agent-releases/${encodeURIComponent(version)}/rollouts`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

export async function getAgentUpdateRollout(rolloutId: string): Promise<AgentUpdateRolloutDetail> {
  return apiRequest<AgentUpdateRolloutDetail>(
    `/api/admin/agent-releases/rollouts/${encodeURIComponent(rolloutId)}`,
  )
}

export async function approveAgentUpdateRollout(rolloutId: string): Promise<AgentUpdateRolloutDetail> {
  return apiRequest<AgentUpdateRolloutDetail>(
    `/api/admin/agent-releases/rollouts/${encodeURIComponent(rolloutId)}/approve`,
    { method: 'POST' },
  )
}

export async function cancelAgentUpdateRollout(rolloutId: string): Promise<AgentUpdateRolloutDetail> {
  return apiRequest<AgentUpdateRolloutDetail>(
    `/api/admin/agent-releases/rollouts/${encodeURIComponent(rolloutId)}/cancel`,
    { method: 'POST' },
  )
}

export async function retryAgentUpdateRolloutNodes(
  rolloutId: string,
  nodeIds: string[],
): Promise<AgentUpdateRolloutDetail> {
  return apiRequest<AgentUpdateRolloutDetail>(
    `/api/admin/agent-releases/rollouts/${encodeURIComponent(rolloutId)}/retry`,
    {
      method: 'POST',
      body: JSON.stringify({ node_ids: nodeIds }),
    },
  )
}
